/**
 * A chat's live file-state notices (GET /chat-files/sessions/{uuid}/events, MHS-646): best effort, so callers
 * refetch the authoritative status (or the file) on each notice and keep polling as the fallback.
 *
 *   for await (const event of streamChatFileEvents(sessionUuid, { signal })) { ... }
 *
 * Frames: `ready` (subscribed: anything may have changed while disconnected), `attachment_state` (one file's new
 * state, validated against the generated AttachmentStateEvent), `revoked` (no more access: the stream ends) and
 * `unavailable` (live progress is off: poll). Like the user stream it has no end of its own: it reconnects with
 * backoff until the signal aborts; a 404 (no access to the chat) ends it.
 */

import { createParser } from "eventsource-parser";
import { reportError } from "@/telemetry/telemetry";
import { ApiError } from "./errors";
import type { AttachmentStateEvent } from "./generated/types.gen";
import { zAttachmentStateEvent } from "./generated/zod.gen";
import { getAccessToken } from "./tokens";

export type ChatFileEvent =
  | { event: "attachment_state"; data: AttachmentStateEvent }
  | { event: "ready" | "revoked" | "unavailable" };

export interface ChatFileEventOptions {
  signal?: AbortSignal;
  /** Base backoff in ms (doubles per failed attempt, capped at maxDelayMs). */
  retryDelayMs?: number;
  maxDelayMs?: number;
  /** Test seam (just the call signature). */
  fetchImpl?: (input: URL, init: RequestInit) => Promise<Response>;
}

const SIGNALS = new Set(["ready", "revoked", "unavailable"]);

export function toChatFileEvent(frame: { event?: string; data: string }): ChatFileEvent | null {
  const name = frame.event ?? "message";
  if (SIGNALS.has(name)) return { event: name as "ready" | "revoked" | "unavailable" };
  let data: unknown;
  try {
    data = JSON.parse(frame.data);
  } catch {
    reportError(new Error("Unparseable chat file event"), { source: "chatFileEvents", event: name });
    return null;
  }
  const parsed = zAttachmentStateEvent.safeParse(data);
  if (name !== "attachment_state" || !parsed.success) {
    reportError(new Error(`Unknown or malformed chat file event "${name}"`), { source: "chatFileEvents" });
    return null;
  }
  return { event: "attachment_state", data: parsed.data as AttachmentStateEvent };
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

type FetchImpl = NonNullable<ChatFileEventOptions["fetchImpl"]>;

async function open(sessionUuid: string, fetchImpl: FetchImpl, signal?: AbortSignal): Promise<Response> {
  const path = `/api/v1/chat-files/sessions/${encodeURIComponent(sessionUuid)}/events`;
  const url = new URL(path, globalThis.location.origin);
  const token = await getAccessToken();
  let response: Response;
  try {
    response = await fetchImpl(url, {
      headers: { Accept: "text/event-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      signal,
    });
  } catch (error) {
    throw new ApiError(0, error instanceof Error ? error.message : "Network error");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, response.statusText, body, response.headers.get("x-request-id"));
  }
  return response;
}

async function* readConnection(response: Response): AsyncGenerator<ChatFileEvent> {
  const queue: ChatFileEvent[] = [];
  const parser = createParser({
    onEvent: (frame) => {
      const event = toChatFileEvent(frame);
      if (event) queue.push(event);
    },
  });
  const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader();
  if (!reader) return;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(value);
      while (queue.length) yield queue.shift() as ChatFileEvent;
    }
  } catch (error) {
    if (!(error instanceof TypeError || error instanceof DOMException)) throw error;
  } finally {
    reader.releaseLock();
  }
}

/** The backoff attempt to resume from after a connection ends (with `error` if it failed); null ends the stream. */
function attemptAfter(error: unknown, attempt: number): number | null {
  if (!(error instanceof ApiError)) return attempt;
  if (error.status === 404) return null; // no access to this chat
  // Live progress unavailable (503) or another client error: retry, but at the slowest pace.
  if (error.status >= 400 && (error.status < 500 || error.status === 503)) return 64;
  return attempt;
}

export async function* streamChatFileEvents(
  sessionUuid: string,
  options: ChatFileEventOptions = {},
): AsyncGenerator<ChatFileEvent> {
  const { signal, retryDelayMs = 2000, maxDelayMs = 60_000, fetchImpl = fetch } = options;
  let attempt = 0;
  while (!signal?.aborted) {
    let failure: unknown;
    try {
      const response = await open(sessionUuid, fetchImpl, signal);
      for await (const event of readConnection(response)) {
        attempt = 0; // progress resets the backoff
        yield event;
        if (event.event === "revoked") return;
      }
    } catch (error) {
      failure = error;
    }
    const resumeAt = attemptAfter(failure, attempt);
    if (signal?.aborted || resumeAt === null) return;
    attempt = resumeAt + 1;
    // Sequential by design: each reconnect waits for the previous attempt's backoff.
    await sleep(Math.min(retryDelayMs * 2 ** (attempt - 1), maxDelayMs), signal); // NOSONAR
  }
}
